import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {ProteinAlignment} from 'constants/AlignmentConstants';
import FieldActionButtons from 'metadata/FieldActionButtons';
import ProteinAlignmentActions from 'molbio/proteins/alignments/ProteinAlignmentActions';
import {ProteinAlignmentSummary} from 'molbio/proteins/util/ProteinConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import FormInput from 'ui-library/bs/FormInput';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import {readableTime} from 'util/DateUtil';
import * as keyboard from 'util/Keyboard';
import * as modals from 'util/Modals';
import {ElementKeyboardEvent, ElementMouseEvent, InputChangeEvent, InputFocusEvent} from 'util/Types';

type ProteinTableAlignment = ProteinAlignment | ProteinAlignmentSummary;
interface ProteinAlignmentPanelTableProps {
  alignments: Array<ProteinTableAlignment>;
  closePanel: () => void;
  showActionButtons: boolean;
  dataTestAttrs?: DataTestAttrs;
}

interface ProteinAlignmentPanelTableState {
  rowUiState: {[alignmentId: string]: AlignmentRowUIState};
}

interface AlignmentRowUIState {
  isEditing: boolean;
  inputValue: string;
}

class ProteinAlignmentPanelTable extends React.Component<
  ProteinAlignmentPanelTableProps,
  ProteinAlignmentPanelTableState
> {
  UNSAFE_componentWillReceiveProps(newProps: ProteinAlignmentPanelTableProps): void {
    this.setState({
      rowUiState: this._mapAlignments(
        newProps.alignments,
        (alignment) => this.state.rowUiState[alignment.id] || this._emptyRowUiState(alignment)
      ),
    });
  }

  _mapAlignments = <T,>(
    alignments: Array<ProteinTableAlignment>,
    func: (alignment: ProteinTableAlignment) => T
  ): {[alignmentId: string]: T} =>
    _.object(
      // eslint-disable-next-line no-restricted-syntax
      _.pluck(alignments, 'id'),
      alignments.map((alignment) => func(alignment))
    );

  _emptyRowUiState = (alignment: ProteinTableAlignment): AlignmentRowUIState => ({
    isEditing: false,
    inputValue: alignment.name,
  });

  _isNotEditingAnyRow = (): boolean =>
    !Object.values(this.state.rowUiState).some((rowState) => rowState.isEditing);

  _handleClickRow = (alignment: ProteinTableAlignment): void => {
    if (this._isNotEditingAnyRow()) {
      ProteinAlignmentActions.open({proteinId: alignment.proteinId, alignmentId: alignment.id});
      this.props.closePanel();
    }
  };

  _deleteAlignmentHandler =
    (alignment: ProteinTableAlignment) =>
    (e: ElementMouseEvent): void => {
      // Prevent row from being clicked
      e.stopPropagation();
      modals
        .confirm(
          'Confirm Delete',
          <span>
            Are you sure you want to delete the alignment <strong>{alignment.name}</strong>? This action is
            not reversible.
          </span>,
          'danger'
        )
        .done(() => {
          ProteinAlignmentActions.delete(alignment.proteinId, alignment.id);
        });
    };

  _getInputValue = (alignmentId: string): string => this.state.rowUiState[alignmentId].inputValue;

  _getIsEditing = (alignmentId: string): boolean => this.state.rowUiState[alignmentId].isEditing;

  _setRowUiState = (alignmentId: string, updateObject: Partial<AlignmentRowUIState>): void => {
    const newState = reactUpdate(this.state, {
      rowUiState: {
        [alignmentId]: {$merge: updateObject},
      },
    });
    this.setState(newState);
  };

  _editHandler =
    (alignmentId: string) =>
    (e: ElementMouseEvent): void => {
      e.stopPropagation();
      this._setRowUiState(alignmentId, {isEditing: true});
    };

  _cancelHandler = (alignmentId: string, alignmentName: string) => (): void => {
    this._setRowUiState(alignmentId, {
      isEditing: false,
      inputValue: alignmentName,
    });
  };

  _completeEditing = (alignment: ProteinTableAlignment): void => {
    const name = this._getInputValue(alignment.id);
    ProteinAlignmentActions.rename(alignment.id, name);
    this._setRowUiState(alignment.id, {isEditing: false});
  };

  _completeEditingHandler = (alignment: ProteinTableAlignment) => (): void => {
    this._completeEditing(alignment);
  };

  _isCompleteEnabled = (alignmentId: string): boolean => Boolean(this._getInputValue(alignmentId));

  _onChangeHandler =
    (alignmentId: string) =>
    (e: InputChangeEvent): void => {
      this._setRowUiState(alignmentId, {inputValue: e.target.value});
    };

  _keyDownHandler =
    (alignment: ProteinTableAlignment) =>
    (e: ElementKeyboardEvent): void => {
      if (keyboard.isEnterKey(e)) {
        e.preventDefault();
        if (this._isCompleteEnabled(alignment.id)) {
          this._completeEditing(alignment);
        }
      } else if (keyboard.isEscapeKey(e)) {
        e.preventDefault();
        e.stopPropagation();
        this._setRowUiState(alignment.id, {isEditing: false});
      }
    };

  _handleOnFocus = (e: InputFocusEvent): void => {
    const input = e.target;
    input.setSelectionRange(0, input.value.length);
  };

  _renderNameColumn = (column: SortableTableColumn, row: ProteinTableAlignment): JSX.Element | string => {
    const alignmentId = row.id;
    if (this._getIsEditing(alignmentId)) {
      return (
        <FormInput
          autoFocus={true}
          onFocus={this._handleOnFocus}
          value={this._getInputValue(alignmentId)}
          onChange={this._onChangeHandler(alignmentId)}
          onKeyDown={this._keyDownHandler(row)}
        />
      );
    } else {
      return row.name;
    }
  };

  _renderActionButtonColumn = (column: SortableTableColumn, row: ProteinTableAlignment): JSX.Element => {
    const alignmentId = row.id;
    return this.props.showActionButtons ? (
      <FieldActionButtons
        isEditing={this._getIsEditing(alignmentId)}
        cancel={this._cancelHandler(alignmentId, row.name)}
        complete={this._completeEditingHandler(row)}
        isCompleteEnabled={this._isCompleteEnabled(alignmentId)}
        remove={this._deleteAlignmentHandler(row)}
        edit={this._editHandler(alignmentId)}
        className="alignmentPanel-actionButtons"
      />
    ) : (
      <></>
    );
  };

  state: ProteinAlignmentPanelTableState = {
    rowUiState: this._mapAlignments(this.props.alignments, this._emptyRowUiState),
  };

  render(): JSX.Element {
    return (
      <SortableTable
        columns={[
          SortableTable.defaultColumn('Name', 'name', {render: this._renderNameColumn}),
          SortableTable.defaultColumn('Created', 'created', {
            render: (column, row: ProteinTableAlignment) => readableTime(row.createdAt, {showTime: false}),
          }),
          SortableTable.defaultColumn('', '', {
            header: '',
            sortable: false,
            render: this._renderActionButtonColumn,
          }),
        ]}
        rows={this.props.alignments}
        onClickRow={this._handleClickRow}
        clickable={this._isNotEditingAnyRow()}
        verticalAlignMiddle={true}
        className="u-small-spacing-top panel-container-table alignmentPanel-table"
        dataTestAttrs={this.props.dataTestAttrs}
      />
    );
  }
}

export default ProteinAlignmentPanelTable;
